<script setup lang="ts">
import type { NuxtError } from '#app';
import MaintainMode from './components/errors/MaintainMode.vue';

defineOptions({
  name: 'ErrorPage',
});

const { t } = useI18n()

const props = defineProps<{
  error: NuxtError<{
    statusCode: number;
    statusMessage: string;
    message: string;
  }>;
}>();

const isMaintenanceMode = computed(() => {
  return props.error.statusMessage === 'MAINTENANCE_MODE';
});

const errorMessageMap: { [key: number]: string } = {
	404: t("We can't find what you are looking for"),
	408: t('The API is taking to long to respond'),
	500: t('Oops, something went terribly wrong'),
	502: t("Server couldn't complete your request. Please try again in few seconds."),
	503: t('Server is really busy right now')
}

const errorMessage =
	props.error.message || errorMessageMap[props.error.statusCode as keyof typeof errorMessageMap]

useHead({
	title: props.error.statusCode === 503 ? t('Maintenance mode') : t('Error'),
	meta: [
		{ name: 'description', content: errorMessage },
		{ name: 'robots', content: 'noindex, nofollow' }
	],
	htmlAttrs: {
		lang: 'lt',
		class: 'font-sans md:text-[14px] lg:text-[15px] xl:text-[16px]'
	}
})

useServerSeoMeta({
	title: props.error.statusCode === 503 ? t('Maintenance mode') : t('Error'),
	description: errorMessage,
	robots: 'noindex, nofollow'
})


if (props.error.statusCode === 412) {
  // setting a timeout here to ensure we are the last error message in terminal
  setTimeout(() => {
    console.error(
      'Looks like your API connection is not working. Check your _nuxt.config.ts_ configuration (shopware.endpoint and shopware.accessToken). 🤞'
    );
    console.error(
      'For more help ➡️  https://frontends.shopware.com/resources/troubleshooting.html'
    );
  }, 2.0 * 1000);
}
</script>

<template>
  <MaintainMode v-if="isMaintenanceMode" />
  <div
    v-else
    class="px-5 py-3 md:py-20 md:px-32 lg:px-24 lg:py-24 items-center flex justify-center flex-col-reverse lg:flex-row"
  >
    <div class="flex flex-col items-center justify-center my-8">
      <div class="max-w-md text-center">
        <h1 class="mb-4 font-extrabold text-9xl">
          <span class="sr-only">{{ $t('error') }}</span
          >{{ props.error.statusCode }}
        </h1>
        <p
          v-if="errorMessage"
          class="text-xl md:text-3xl font-semibold mt-4 mb-6"
        >
          {{ errorMessage }}
        </p>
        
        <NuxtLink
          to="/"
        >
          Eiti į pradinį puslapį
        </NuxtLink>
      </div>
    </div>
    <div class="flex items-center justify-center">
      <img
        class="w-full h-full max-w-md max-h-md"
        src="~/assets/error-background.png"
        alt="Error"
      />
    </div>
  </div>
</template>
